import { Params } from '@angular/router';

/**
 * Marketing-attribution query-param keys that are forwarded across navigation.
 *
 * Includes the `utm_*` family plus paid-search / social click IDs that the ad
 * platforms use server-side to reconcile clicks to conversions. Click IDs
 * routinely arrive without matching `utm_*` (Google Auto-tagging in particular)
 * so a pure `utm_` prefix filter breaks Google Ads / Meta CAPI match-back.
 */
export const ATTRIBUTION_PARAM_KEYS: readonly string[] = [
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'li_fat_id',
  'wbraid',
  'gbraid',
  'dclid',
  'irclid'
];

export const UTM_PARAM_PREFIX = 'utm_';

/** Whether a query-param key is an attribution-tracking key. */
export const isAttributionParamKey = (key: string): boolean => key.startsWith(UTM_PARAM_PREFIX) || ATTRIBUTION_PARAM_KEYS.includes(key);

// Split URL on first '#'. Runs BEFORE splitQuery so a '?' inside the fragment
// (RFC 3986: fragments can contain '?') stays part of the hash, not the query.
const splitHash = (url: string): [string, string] => {
  const hashIndex = url.indexOf('#');
  return hashIndex === -1 ? [url, ''] : [url.slice(0, hashIndex), url.slice(hashIndex)];
};

const splitQuery = (url: string): [string, string] => {
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? [url, ''] : [url.slice(0, queryIndex), url.slice(queryIndex + 1)];
};

const coerceValue = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    const first: unknown = value.find((v) => v !== null && v !== undefined && v !== '');
    return first === null || first === undefined ? null : String(first);
  }
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return String(value);
};

/**
 * Append marketing-attribution params (`utm_*` plus paid-search/social click
 * IDs — see {@link ATTRIBUTION_PARAM_KEYS}) onto a destination URL so
 * attribution survives navigation to the marketing site, match flow, or other
 * outbound destinations.
 *
 * Behavior:
 * - Forwards any attribution key present in {@link isAttributionParamKey}.
 * - Skips empty / null / undefined values, and takes the first non-empty entry
 *   when an attribution param arrives as `string[]`.
 * - Current-route attribution values WIN over any pre-existing values on the
 *   target URL — `URLSearchParams.set` overwrites. This is intentional so
 *   first-touch attribution propagates correctly.
 * - Preserves any non-attribution query string and `#fragment` already on the
 *   target URL.
 * - SSR-safe: takes params explicitly rather than reading `window.location`,
 *   and is fail-soft (never throws — returns the input URL on any error).
 *
 * @param url - The destination URL (absolute or relative) to append params to.
 * @param params - The current route's query params (e.g. `ActivatedRoute.snapshot.queryParams`).
 * @returns The URL with current attribution params appended, or {@link url} unchanged.
 */
export const appendAttributionParams = (url: string, params: Params | null | undefined): string => {
  if (!params || typeof params !== 'object') {
    return url;
  }

  try {
    const entries: Array<[string, string]> = Object.entries(params)
      .filter(([key]) => isAttributionParamKey(key))
      .map(([key, value]): [string, string | null] => [key, coerceValue(value)])
      .filter((entry): entry is [string, string] => entry[1] !== null);

    if (entries.length === 0) {
      return url;
    }

    const [base, hash] = splitHash(url);
    const [path, existingQuery] = splitQuery(base);

    const search = new URLSearchParams(existingQuery);
    entries.forEach(([key, value]) => search.set(key, value));

    return `${path}?${search.toString()}${hash}`;
  } catch {
    return url;
  }
};

/** Returns only the attribution-relevant subset of {@link params}, omitting empty values. */
export const pickAttributionParams = (params: Params | null | undefined): Params => {
  if (!params || typeof params !== 'object') {
    return {};
  }
  return Object.fromEntries(
    Object.entries(params)
      .filter(([key]) => isAttributionParamKey(key))
      .map(([key, value]): [string, string | null] => [key, coerceValue(value)])
      .filter((entry): entry is [string, string] => entry[1] !== null)
  );
};
